import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Loader2 } from 'lucide-react';

export interface FormatOption {
  format: string;
  label: string;
}

export const FORMAT_GROUPS: { title: string; options: FormatOption[] }[] = [
  { title: 'Pix', options: [{ format: 'carre_13', label: '13x13cm' }] },
  {
    title: 'Carré',
    options: [
      { format: 'carre_21', label: '21x21cm' },
      { format: 'carre_30', label: '30x30cm' },
    ],
  },
  {
    title: 'Portrait',
    options: [
      { format: 'a4', label: 'A4' },
      { format: 'a5', label: 'A5' },
    ],
  },
  {
    title: 'Paysage',
    options: [
      { format: 'a4_paysage', label: 'A4' },
      { format: 'a5_paysage', label: 'A5' },
    ],
  },
  {
    title: 'Premium',
    options: [
      { format: 'carre_fuji_20', label: '20x20cm' },
      { format: 'carre_fuji_30', label: '29x29cm' },
    ],
  },
];

const PAGE_OPTIONS = Array.from({ length: 21 }, (_, i) => 20 + i * 4); // 20 → 100

interface EditFormatDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  socialNetworkId: number | null;
  currentFormat?: string | null;
  currentPageCount?: number;
}

const EditFormatDialog: React.FC<EditFormatDialogProps> = ({
  open,
  onOpenChange,
  socialNetworkId,
  currentFormat,
  currentPageCount,
}) => {
  const [format, setFormat] = useState<string>(currentFormat || 'a4');
  const [numberPage, setNumberPage] = useState<number>(() => {
    const base = currentPageCount ?? 32;
    return PAGE_OPTIONS.reduce((best, v) => (Math.abs(v - base) < Math.abs(best - base) ? v : best), PAGE_OPTIONS[0]);
  });

  // Le format courant arrive du load (asynchrone) : on resynchronise à chaque ouverture de la popup
  useEffect(() => {
    if (open && currentFormat) setFormat(currentFormat);
  }, [open, currentFormat]);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!socialNetworkId) return;
    setLoading(true);
    try {
      const url =
        `/social_networks/${socialNetworkId}/yearbooks/generate_album_magic` +
        `?type_generation=new_format&number_page=${numberPage}` +
        `&format_of_yearbook=${format}&status=save&type_cover=album`;
      await fetch(url, { headers: { Accept: 'application/json' } });
    } catch (e) {
      console.warn('[EditFormat] request failed', e);
    } finally {
      window.location.reload();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Modifier le format de mon album</DialogTitle>
          <DialogDescription>Choisissez le format et le nombre de pages souhaités.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Format et taille</p>
            {FORMAT_GROUPS.map((group) => (
              <div key={group.title} className="flex items-center gap-2">
                <span className="w-20 shrink-0 text-xs text-muted-foreground">{group.title}</span>
                <div className="flex flex-wrap gap-1.5">
                  {group.options.map((opt) => (
                    <button
                      key={opt.format}
                      type="button"
                      onClick={() => setFormat(opt.format)}
                      className={`px-3 py-1.5 rounded-md text-xs border transition-colors ${
                        format === opt.format
                          ? 'bg-primary text-primary-foreground border-transparent'
                          : 'border-border hover:bg-accent'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground" htmlFor="number-page">
              Nombre de pages
            </label>
            <select
              id="number-page"
              value={numberPage}
              onChange={(e) => setNumberPage(Number(e.target.value))}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            >
              {PAGE_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n} pages
                </option>
              ))}
            </select>
          </div>

          <p className="text-xs text-destructive">(La modification de l'album remplacera la mise en page actuelle)</p>

          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading || !socialNetworkId}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-md bg-primary-gradient text-primary-foreground text-sm font-medium hover:opacity-90 disabled:opacity-50"
          >
            {loading && <Loader2 size={14} className="animate-spin" />}
            Modifier mon album
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditFormatDialog;